import { Controller } from "@hotwired/stimulus"

// Filter bar for the partner Configuration > Users page (staff.html.erb).
// Mirrors the same UX pattern as the Claims Dashboard filter bar
// (see claims_filter_controller.js): an "Add filters" button, a dropdown
// with a left category list and a right contextual panel, chips for active
// filters, and Apply/Cancel/Clear all actions. Unlike the claims filter,
// this one filters rows that are already rendered on the page (no server
// round trip) — it sets a `hiddenByFilter` flag on each row's dataset and
// dispatches a "changed" event that the sibling `users-search` controller
// (which owns the text-search flag, count, and empty state) listens for to
// recompute combined visibility.
export default class extends Controller {
  static targets = [
    "toggleButton", "buttonLabel", "noFiltersText",
    "chipsContainer", "overflowButton", "clearAllButton",
    "dropdown", "categoryList", "rightPanel", "draftCount"
  ]

  static values = {
    roleOptions: Array,
    permissionOptions: Array
  }

  connect() {
    this.MAX_VISIBLE_CHIPS = 3
    this.categories = [
      { key: "role", label: "Role", type: "multiselect", options: this.roleOptionsValue },
      { key: "has_permission", label: "Has Permissions", type: "multiselect", options: this.permissionOptionsValue },
      { key: "added_date", label: "Added Date", type: "date_range" }
    ]

    this.activeFilters = {}
    this.draftFilters = {}
    this.selectedCategory = this.categories[0].key
    this.isOpen = false

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("mousedown", this.handleClickOutside)

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)

    this.renderChips()
    this.updateButtonLabel()
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  // --- Dropdown open/close ---

  toggleDropdown(event) {
    event?.stopPropagation()
    this.isOpen ? this.closeDropdown() : this.openDropdown()
  }

  openDropdown() {
    this.draftFilters = JSON.parse(JSON.stringify(this.activeFilters))
    this.isOpen = true
    this.dropdownTarget.classList.remove("hidden")
    this.renderCategoryList()
    this.renderRightPanel()
    this.updateDraftCount()
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
  }

  cancel() {
    this.closeDropdown()
  }

  handleClickOutside(event) {
    if (!this.isOpen) return
    if (this.element.contains(event.target)) return
    this.closeDropdown()
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    if (event.key === "Escape") this.closeDropdown()
  }

  onDropdownClick(event) {
    event.stopPropagation()
  }

  // --- Apply / Clear ---

  apply() {
    this.activeFilters = JSON.parse(JSON.stringify(this.draftFilters))
    this.closeDropdown()
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  clearAll() {
    this.activeFilters = {}
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  removeChip(key, value) {
    const filters = JSON.parse(JSON.stringify(this.activeFilters))
    if (filters[key]) {
      filters[key] = filters[key].filter(v => v !== value)
      if (filters[key].length === 0) delete filters[key]
    }
    this.activeFilters = filters
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  // --- Row filtering (client-side; no server round trip) ---

  applyFiltersToRows() {
    // The users table lives outside this controller's own element (it's a
    // sibling further down inside the shared `users-search` wrapper), so
    // scope the row lookup to that shared ancestor rather than `this.element`.
    const scope = this.element.closest('[data-controller~="users-search"]') || document
    const rows = Array.from(scope.querySelectorAll('[data-users-search-target="row"]'))

    rows.forEach(row => {
      let hidden = false

      const roleValues = this.activeFilters.role || []
      if (roleValues.length > 0) {
        const rowRoles = this.parseJsonArray(row.dataset.userRoles)
        hidden = hidden || !roleValues.some(v => rowRoles.includes(v))
      }

      const permissionValues = this.activeFilters.has_permission || []
      if (permissionValues.length > 0) {
        const rowPermissions = this.parseJsonArray(row.dataset.userPermissions)
        hidden = hidden || !permissionValues.some(v => rowPermissions.includes(v))
      }

      const dateValues = this.activeFilters.added_date || []
      if (dateValues.length > 0) {
        hidden = hidden || !this.dateInRange(row.dataset.userAddedAt, dateValues[0])
      }

      row.dataset.hiddenByFilter = hidden ? "1" : ""
    })

    this.dispatch("changed", { detail: { filters: this.activeFilters } })
  }

  dateInRange(addedAtIso, rangeValue) {
    if (!addedAtIso) return false
    const addedAt = new Date(`${addedAtIso}T00:00:00`)

    let fromStr = ""
    let toStr = ""
    if (this.isRelativeDateRange(rangeValue)) {
      const preset = this.relativePresetKey(rangeValue)
      ;[fromStr, toStr] = this.resolveRelativeDateRange(preset)
    } else {
      [fromStr, toStr] = rangeValue.split("|")
    }

    if (fromStr) {
      const from = new Date(`${fromStr}T00:00:00`)
      if (addedAt < from) return false
    }
    if (toStr) {
      const to = new Date(`${toStr}T23:59:59`)
      if (addedAt > to) return false
    }
    return true
  }

  // --- Rendering: chips ---

  updateButtonLabel() {
    const count = this.getAllChips().length
    this.buttonLabelTarget.textContent = count > 0 ? `Filters (${count})` : "Add filters"
  }

  findOption(category, value) {
    return (category.options || []).find(opt => opt.value === value)
  }

  getAllChips() {
    const chips = []
    for (const [key, values] of Object.entries(this.activeFilters)) {
      const category = this.categories.find(c => c.key === key)
      if (!category) continue
      for (const val of values) {
        let label
        if (category.type === "multiselect") {
          const opt = this.findOption(category, val)
          label = opt ? opt.label : val
        } else if (category.type === "date_range") {
          label = `${category.label}: ${this.formatDateRange(val)}`
        } else {
          label = val
        }
        chips.push({ key, value: val, label })
      }
    }
    return chips
  }

  renderChips() {
    const allChips = this.getAllChips()
    const visibleChips = allChips.slice(0, this.MAX_VISIBLE_CHIPS)
    const overflowCount = allChips.length - this.MAX_VISIBLE_CHIPS
    const hasFilters = allChips.length > 0

    this.chipsContainerTarget.innerHTML = ""
    for (const chip of visibleChips) {
      this.chipsContainerTarget.appendChild(this.createChipElement(chip))
    }

    this.noFiltersTextTarget.classList.toggle("hidden", hasFilters)

    if (overflowCount > 0) {
      this.overflowButtonTarget.textContent = `+${overflowCount} more`
      this.overflowButtonTarget.classList.remove("hidden")
    } else {
      this.overflowButtonTarget.classList.add("hidden")
    }

    this.clearAllButtonTarget.classList.toggle("hidden", !hasFilters)
  }

  createChipElement(chip) {
    const el = document.createElement("div")
    el.className = "px-4 py-2 rounded-lg justify-end items-center gap-4 inline-flex border bg-support-light-250 text-support-dark-400 border-support-light-350 hover:bg-support-light-300 hover:text-support-dark-500 hover:border-support-light-400 whitespace-nowrap"

    const labelSpan = document.createElement("div")
    labelSpan.className = "text-xs font-semibold leading-[18px]"
    labelSpan.textContent = chip.label
    el.appendChild(labelSpan)

    const btn = document.createElement("div")
    btn.className = "cursor-pointer"
    btn.setAttribute("role", "button")
    btn.setAttribute("aria-label", `Remove ${chip.label}`)
    btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 32 32" fill="none" class="flex-shrink-0"><path d="M25 7L7 25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M25 25L7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.removeChip(chip.key, chip.value)
    })
    el.appendChild(btn)

    return el
  }

  // --- Rendering: category list ---

  renderCategoryList() {
    const container = this.categoryListTarget
    container.innerHTML = ""

    const eyebrow = document.createElement("div")
    eyebrow.className = "text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em] px-3 pt-3 pb-1.5"
    eyebrow.textContent = "FILTER BY"
    container.appendChild(eyebrow)

    for (const category of this.categories) {
      const isActive = this.selectedCategory === category.key
      const count = (this.draftFilters[category.key] || []).length

      const btn = document.createElement("button")
      btn.className = `flex items-center gap-2 text-left cursor-pointer rounded-md transition-all duration-100 py-[7px] px-[10px] text-[13px] border ${
        isActive
          ? "bg-core-purple-200 border-core-purple-300 font-semibold text-core-purple-500"
          : "bg-transparent border-transparent font-medium text-gray-700 hover:bg-support-light-250 hover:border-support-light-350"
      }`
      btn.style.width = "calc(100% - 16px)"
      btn.style.margin = "0 8px"

      btn.innerHTML = `
        ${count > 0 ? this.checkCircleIcon() : this.categoryIcon(category.type)}
        <span class="overflow-hidden text-ellipsis whitespace-nowrap flex-1">${category.label}</span>
      `

      btn.addEventListener("click", () => {
        this.selectedCategory = category.key
        this.renderCategoryList()
        this.renderRightPanel()
      })

      container.appendChild(btn)
    }
  }

  checkCircleIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" class="flex-shrink-0 fill-core-purple-500"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"/></svg>`
  }

  categoryIcon(type) {
    if (type === "date_range") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <rect x="1.5" y="2.5" width="13" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
        <path d="M1.5 6.5h13" stroke="currentColor" stroke-width="1.5"/>
        <path d="M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`
    }
    return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
      <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/>
      <path d="M9 3.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <rect x="1" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/>
      <path d="M9 11.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`
  }

  // --- Rendering: right panel ---

  renderRightPanel() {
    const category = this.categories.find(c => c.key === this.selectedCategory)
    if (!category) return

    const panel = this.rightPanelTarget
    panel.innerHTML = ""

    if (category.type === "multiselect") {
      this.renderMultiselectPanel(panel, category)
    } else if (category.type === "date_range") {
      this.renderDateRangePanel(panel, category)
    }
  }

  // --- Multiselect panel (Role / Has Permissions) ---

  renderMultiselectPanel(container, category) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 overflow-y-auto p-4 flex flex-col gap-0.5"

    const rows = []

    const optState = (opt) => {
      const currentSelected = this.draftFilters[category.key] || []
      return currentSelected.includes(opt.value) ? "checked" : "unchecked"
    }

    const checkboxIcon = (state) => {
      if (state === "checked") {
        return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      }
      return ""
    }

    const applyRowStyles = (row, opt) => {
      const state = optState(opt)
      const isHighlighted = state !== "unchecked"
      row.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
        isHighlighted ? "bg-core-purple-200" : "bg-transparent hover:bg-gray-50"
      }`
      const checkbox = row.querySelector("[data-checkbox]")
      if (checkbox) {
        checkbox.className = `w-[18px] h-[18px] rounded flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
          isHighlighted ? "border-core-purple-500 bg-core-purple-500" : "border-gray-300 bg-white"
        }`
        checkbox.innerHTML = checkboxIcon(state)
      }
    }

    const buildRow = (opt) => {
      const row = document.createElement("label")
      row.dataset.optValue = opt.value
      const isChecked = optState(opt) === "checked"
      row.innerHTML = `
        <input type="checkbox" class="sr-only" ${isChecked ? "checked" : ""} aria-label="${this.escapeHtml(opt.label)}">
        <div data-checkbox></div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold text-gray-800 leading-tight">${this.escapeHtml(opt.label)}</div>
          ${opt.description ? `<div class="text-xs text-support-dark-400 mt-0.5 leading-snug">${this.escapeHtml(opt.description)}</div>` : ""}
        </div>
      `
      applyRowStyles(row, opt)
      return row
    }

    for (const opt of (category.options || [])) {
      const row = buildRow(opt)
      const input = row.querySelector('input[type="checkbox"]')
      input.addEventListener("change", () => {
        this.toggleDraftValue(category.key, opt.value)
        rows.forEach(item => {
          const itemInput = item.row.querySelector('input[type="checkbox"]')
          itemInput.checked = optState(item.opt) === "checked"
          applyRowStyles(item.row, item.opt)
        })
        this.renderCategoryList()
        this.updateDraftCount()
      })
      rows.push({ row, opt })
      wrapper.appendChild(row)
    }

    container.appendChild(wrapper)
  }

  // --- Date range panel (Added Date) ---

  renderDateRangePanel(container, category) {
    const currentValues = this.draftFilters[category.key] || []
    let fromVal = ""
    let toVal = ""
    let relativePreset = null
    if (currentValues.length > 0) {
      const currentValue = currentValues[0] || ""
      if (this.isRelativeDateRange(currentValue)) {
        relativePreset = this.relativePresetKey(currentValue)
        ;[fromVal, toVal] = this.resolveRelativeDateRange(relativePreset)
      } else {
        const parsed = currentValue.split("|")
        fromVal = parsed[0] || ""
        toVal = parsed[1] || ""
      }
    }

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const dateRow = document.createElement("div")
    dateRow.className = "flex gap-3 items-center"

    const fromDiv = document.createElement("div")
    fromDiv.className = "flex-1"
    fromDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">From</div>`
    const fromInput = document.createElement("input")
    fromInput.type = "date"
    fromInput.value = fromVal
    fromInput.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"
    fromDiv.appendChild(fromInput)

    const arrow = document.createElement("div")
    arrow.className = "text-gray-300 text-base mt-[18px]"
    arrow.textContent = "→"

    const toDiv = document.createElement("div")
    toDiv.className = "flex-1"
    toDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">To</div>`
    const toInput = document.createElement("input")
    toInput.type = "date"
    toInput.value = toVal
    toInput.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"
    toDiv.appendChild(toInput)

    dateRow.append(fromDiv, arrow, toDiv)
    wrapper.appendChild(dateRow)

    const commitDates = (f, t, relative = null) => {
      if (!f && !t && !relative) {
        delete this.draftFilters[category.key]
      } else if (relative) {
        this.draftFilters[category.key] = [`relative:${relative}`]
      } else {
        this.draftFilters[category.key] = [`${f}|${t}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    fromInput.addEventListener("change", () => {
      fromVal = fromInput.value
      relativePreset = null
      commitDates(fromVal, toVal)
    })
    toInput.addEventListener("change", () => {
      toVal = toInput.value
      relativePreset = null
      commitDates(fromVal, toVal)
    })

    const presetsDiv = document.createElement("div")
    presetsDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-2 uppercase tracking-wider">Quick select</div>`

    const presets = [
      { label: "Today", key: "today", fn: () => this.resolveRelativeDateRange("today") },
      { label: "Last 7 days", key: "last_7_days", fn: () => this.resolveRelativeDateRange("last_7_days") },
      { label: "Last 30 days", key: "last_30_days", fn: () => this.resolveRelativeDateRange("last_30_days") },
      { label: "Last 90 days", key: "last_90_days", fn: () => this.resolveRelativeDateRange("last_90_days") },
      { label: "This month", key: "this_month", fn: () => this.resolveRelativeDateRange("this_month") },
      { label: "This year", key: "this_year", fn: () => this.resolveRelativeDateRange("this_year") }
    ]

    const currentPresetLabel = relativePreset ? presets.find(p => p.key === relativePreset)?.label : this.matchPreset(fromVal, toVal, presets)

    const select = document.createElement("select")
    select.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500 cursor-pointer"

    const defaultOpt = document.createElement("option")
    defaultOpt.value = ""
    defaultOpt.textContent = "Select a range..."
    if (!currentPresetLabel) defaultOpt.selected = true
    select.appendChild(defaultOpt)

    for (const p of presets) {
      const opt = document.createElement("option")
      opt.value = p.label
      opt.textContent = p.label
      if (currentPresetLabel === p.label) opt.selected = true
      select.appendChild(opt)
    }

    select.addEventListener("change", () => {
      const chosen = presets.find(p => p.label === select.value)
      if (chosen) {
        const [f, t] = chosen.fn()
        fromInput.value = f
        toInput.value = t
        fromVal = f
        toVal = t
        relativePreset = chosen.key
        commitDates(f, t, chosen.key)
      }
    })

    presetsDiv.appendChild(select)
    wrapper.appendChild(presetsDiv)

    if (fromVal || toVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear dates"
      clearBtn.addEventListener("click", () => {
        fromInput.value = ""
        toInput.value = ""
        fromVal = ""
        toVal = ""
        relativePreset = null
        commitDates("", "")
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  matchPreset(fromVal, toVal, presets) {
    if (!fromVal && !toVal) return null
    for (const p of presets) {
      const [f, t] = p.fn()
      if (f === fromVal && t === toVal) return p.label
    }
    return null
  }

  // --- Draft helpers ---

  toggleDraftValue(key, value) {
    const current = this.draftFilters[key] || []
    if (current.includes(value)) {
      const next = current.filter(v => v !== value)
      if (next.length === 0) {
        delete this.draftFilters[key]
      } else {
        this.draftFilters[key] = next
      }
    } else {
      this.draftFilters[key] = [...current, value]
    }
  }

  updateDraftCount() {
    const total = Object.values(this.draftFilters).reduce((sum, arr) => sum + arr.length, 0)
    this.draftCountTarget.textContent = total > 0
      ? `${total} filter${total !== 1 ? "s" : ""} selected`
      : "No filters selected"
  }

  // --- Date helpers ---

  isoToday() {
    const d = new Date()
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  isRelativeDateRange(value) {
    return (value || "").startsWith("relative:")
  }

  relativePresetKey(value) {
    return (value || "").replace(/^relative:/, "")
  }

  relativePresetLabel(value) {
    const labels = {
      today: "Today",
      last_7_days: "Last 7 days",
      last_30_days: "Last 30 days",
      last_90_days: "Last 90 days",
      this_month: "This month",
      this_year: "This year"
    }
    return labels[this.relativePresetKey(value)] || value
  }

  resolveRelativeDateRange(preset) {
    const today = this.isoToday()
    if (preset === "today") return [today, today]
    if (preset === "last_7_days") return [this.isoOffset(-7), today]
    if (preset === "last_30_days") return [this.isoOffset(-30), today]
    if (preset === "last_90_days") return [this.isoOffset(-90), today]
    if (preset === "this_month") {
      const d = new Date()
      return [`${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-01`, today]
    }
    if (preset === "this_year") return [`${new Date().getFullYear()}-01-01`, today]
    return ["", ""]
  }

  isoOffset(days) {
    const d = new Date()
    d.setDate(d.getDate() + days)
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  pad(n) {
    return String(n).padStart(2, "0")
  }

  formatDateRange(val) {
    if (!val) return ""
    if (this.isRelativeDateRange(val)) return this.relativePresetLabel(val)
    const [from, to] = val.split("|")
    const fmt = (d) => {
      if (!d) return ""
      const dt = new Date(`${d}T00:00:00`)
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    }
    if (from && to) return `${fmt(from)} – ${fmt(to)}`
    if (from) return `After ${fmt(from)}`
    if (to) return `Before ${fmt(to)}`
    return ""
  }

  // --- Utility ---

  parseJsonArray(value) {
    if (!value) return []
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
